import { useMemo } from 'react';
import { useApiService } from './apiService';

export interface PowerBiConnection {
  id: string; name: string; tenantId: string; clientId: string; revision: string;
  accountName?: string; workspaceId?: string; workspaceName?: string; modelId?: string; modelName?: string;
  status: string; lastChecked?: string; lastSuccessfulCheck?: string; lastFailure?: string;
}
export interface PowerBiSource { id: string; name: string }
export interface PowerBiResult {
  status: 'succeeded' | 'failed' | 'incomplete'; elapsedMs: number; returnedRowCount: number;
  columns: { name: string; type?: string }[]; rows: Record<string, unknown>[]; previewTruncated: boolean; message?: string;
}
export interface PowerBiDraft {
  id: string; version: number; connectionId: string; workspaceId: string; modelId: string;
  name: string; dax: string; owner: string; editor: string; createdAt: string; testOutcome?: string;
}
export interface PowerBiDetails { name: string; tenantId: string; clientId: string; clientSecret?: string; revision?: string }
export function usePowerBiApi() {
  const api = useApiService();
  return useMemo(() => {
    const base = '/settings/integrations/powerbi';
    const connection = (id: string) => `${base}/connections/${encodeURIComponent(id)}`;
    return {
      capabilities: () => api.get<{ configured: boolean; callbackUrl: string }>(`${base}/capabilities`),
      connections: () => api.get<PowerBiConnection[]>(`${base}/connections`),
      create: (input: PowerBiDetails) => api.post<PowerBiConnection>(`${base}/connections`, input),
      edit: (id: string, input: PowerBiDetails) => api.put<PowerBiConnection>(connection(id), input),
      authorize: (id: string) => api.post<{ url: string }>(`${connection(id)}/authorize`, {}),
      disconnect: (id: string, revision: string) => api.post<PowerBiConnection>(`${connection(id)}/disconnect`, { revision }),
      workspaces: (id: string) => api.get<PowerBiSource[]>(`${connection(id)}/workspaces`),
      models: (id: string, workspace: string) => api.get<PowerBiSource[]>(`${connection(id)}/workspaces/${encodeURIComponent(workspace)}/models`),
      source: (id: string, workspaceId: string, modelId: string, revision: string) => api.put<PowerBiConnection>(`${connection(id)}/source`, { workspaceId, modelId, revision }),
      verify: (id: string) => api.post<{ connection: PowerBiConnection; checks: { name: string; status: string; message: string }[] }>(`${connection(id)}/verify`, {}),
      run: (id: string, dax: string, queryId?: string, queryVersion?: number) => api.post<PowerBiResult>(`${connection(id)}/test`, { dax, queryId, queryVersion }),
      drafts: (id: string) => api.get<PowerBiDraft[]>(`${connection(id)}/drafts`),
      save: (id: string, name: string, dax: string, expectedVersion: number, queryId?: string) => api.post<PowerBiDraft>(`${connection(id)}/drafts`, { name, dax, expectedVersion, id: queryId }),
    };
  }, [api]);
}

export function powerBiError(error: unknown): string {
  if (!(error instanceof Error)) return 'The Power BI request failed. Refresh and retry.';
  const json = error.message.indexOf('{');
  if (json >= 0) {
    try { const problem = JSON.parse(error.message.slice(json)); if (typeof problem.title === 'string') return problem.title; } catch { /* Transport errors have no problem body. */ }
  }
  return 'The Power BI request could not complete. Refresh to check its status before retrying.';
}
